import { Trans } from '@lingui/macro'
import {
  BackButton,
  ButtonOutline,
  ButtonPrimary
} from 'front-end/src/components'
import {
  Topbar,
  TopbarLeft,
  TopbarRight,
  TopbarTitle
} from 'front-end/src/components/common/topbar/Topbar'
import React, { useState } from 'react'
import { navigateTo } from '../../../app/util/appHistory'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { Box, Flex, Portal, Tab, TabList, Tabs } from '@chakra-ui/react'
import IconPencil from '../../../components/common/icon/IconPencil'
import { useLocation } from 'react-router-dom'
import IconPeople from '../../../components/common/icon/IconPeople'
import { PathwayDetailFieldsForManageFragment } from '../ManagePathwayPage.codegen'
import {
  PathwayInput,
  PathwayState
} from '../../../graphql/private/PrivateSchemaTypes'
import IconShare from '../../../components/common/icon/IconShare'
import IconSettings from '../../../components/common/icon/IconSettings'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import PathwayShareModal from '../../manage-pathways-page/components/PathwayShareModal'
import { AccessModalMode } from '../../manage-pathways-page/components/GenericAccessModal'
import { PathwaySettingsDrawer } from './PathwaySettingsDrawer'
import { SavingStatus } from '../ManagePathwayPage'
import Spinner, {
  SpinnerSizeEnum
} from '../../../components/common/spinner/Spinner'
import { brandColors } from '../../../../../shared/basicPalette'
import { AdminOnlyAllowed } from '../../../components/common/AdminOnlyAllowed'
import IconLock from '../../../components/common/icon/IconLock'
import { StatusDraftGrey } from '../../../components/common/status/Status'

export const ManagePathwayTopbar = ({
  pathway,
  onShare,
  sendPathwayPatch,
  savingStatus
}: {
  pathway: PathwayDetailFieldsForManageFragment
  onShare: () => void
  sendPathwayPatch: (patch: PathwayInput) => void
  savingStatus: SavingStatus
}) => {
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const { pathname } = useLocation()
  const isAccess = pathname.endsWith('/access')

  return (
    <>
      <Topbar>
        <TopbarLeft>
          <BackButton mr="4px" />
          <TopbarTitle>
            <Flex align="center">
              {pathway.title}
              <Flex ml="8px">
                {pathway.state === PathwayState.draft && <StatusDraftGrey />}
                {pathway.state === PathwayState.published &&
                  !pathway.is_public && (
                    <Tooltip tabIndex={-1} text={<Trans>Private</Trans>}>
                      <IconLock position="relative" top="2px" />
                    </Tooltip>
                  )}
              </Flex>
            </Flex>
          </TopbarTitle>
        </TopbarLeft>

        <Tabs size="lg" defaultIndex={isAccess ? 1 : 0}>
          <TabList borderBottom={0}>
            <Tab
              borderRadius={0}
              onClick={() =>
                navigateTo(linkBuilder.pathwayDetailManage(pathway.id))
              }
            >
              <IconPencil mr="8px" /> <Trans>Edit</Trans>
            </Tab>
            <Tab
              borderRadius={0}
              onClick={() =>
                navigateTo(linkBuilder.pathwayDetailManageMembers(pathway.id))
              }
            >
              <IconPeople mr="8px" /> <Trans>Access</Trans>
            </Tab>
          </TabList>
        </Tabs>

        <TopbarRight>
          <AdminOnlyAllowed allowManagerAccess>
            <Box
              mr="12px"
              color={brandColors.grey['mid']}
              data-test="SavingStatus"
            >
              {savingStatus === SavingStatus.SAVED ? (
                <Trans>Saved</Trans>
              ) : savingStatus === SavingStatus.SAVING ? (
                <Spinner size={SpinnerSizeEnum.small} />
              ) : (
                ''
              )}
            </Box>
            {isAccess && (
              <ButtonPrimary
                onClick={() => setShowShareModal(true)}
                px="12px"
                mr="8px"
                leftIcon={<IconShare />}
              >
                <Trans>Share</Trans>
              </ButtonPrimary>
            )}
            {!isAccess && pathway?.state === PathwayState.draft && (
              <ButtonPrimary
                mr="8px"
                // @ts-ignore
                onClick={() => sendPathwayPatch({ published_date: new Date() })}
                disabled={pathway.currentTopicVersionsCount === 0}
              >
                <Trans>Publish</Trans>
              </ButtonPrimary>
            )}
            {pathway?.state !== PathwayState.draft && (
              <ButtonOutline
                mr="8px"
                onClick={() =>
                  navigateTo(linkBuilder.pathwayDetail(pathway.id))
                }
                px="12px"
                dataTest="ViewPathwayButton"
              >
                <Trans>View pathway</Trans>
              </ButtonOutline>
            )}
            {!isAccess && (
              <Tooltip tabIndex={-1} text={<Trans>Settings</Trans>}>
                <ButtonOutline
                  iconOnly
                  isActive={showSettingsDrawer}
                  onClick={() => setShowSettingsDrawer(!showSettingsDrawer)}
                >
                  <IconSettings />
                </ButtonOutline>
              </Tooltip>
            )}
          </AdminOnlyAllowed>
        </TopbarRight>
      </Topbar>

      <PathwaySettingsDrawer
        pathway={pathway}
        show={showSettingsDrawer}
        onHide={() => setShowSettingsDrawer(false)}
      />

      <Portal>
        <PathwayShareModal
          show={showShareModal}
          pathwayId={pathway.id}
          onCancel={() => setShowShareModal(false)}
          onSubmitted={() => {
            setShowShareModal(false)
            onShare()
          }}
          mode={AccessModalMode.ENROL_AND_ACCESS}
        />
      </Portal>
    </>
  )
}
